import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Search, MapPin, Star, Calendar, Shield, MessageCircle, ArrowRight, Users, BookOpen, Globe, CheckCircle, Heart, FileSignature } from 'lucide-react';
import { BrandLogo } from '../components/BrandLogo';
import { FacebookIcon } from '../components/FacebookIcon';
import { InstagramIcon } from '../components/InstagramIcon';
import { ReviewPromoBox } from '../components/reviews/ReviewPromoBox';
import { ReviewCarousel } from '../components/reviews/ReviewCarousel';

export default function LandingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { t, lang, setLang, languages } = useLanguage();
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [showDeleted, setShowDeleted] = useState(searchParams.get('deleted') === '1');
  const langRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (langRef.current && !langRef.current.contains(e.target)) setShowLangMenu(false); };
    if (showLangMenu) document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [showLangMenu]);

  const features = [
    { icon: Search, title: lang === 'fr' ? 'Recherche intelligente' : 'Smart Search', desc: lang === 'fr' ? 'Trouvez des garderies, nounous, gardiennes, aidants et fournisseurs filtrés selon vos besoins exacts, peu importe où vous êtes, même en vacances. Enregistrez vos fournisseurs favoris et recevez des notifications en temps réel des ouvertures à temps partiel, à temps plein ou sans rendez-vous.\n\nConsultez la liste des camps et programmes dans votre région ou découvrez les événements à venir!' : 'Find daycares, nannies, babysitters, caregivers and providers filtered by your exact needs, no matter where you are, even if you\'re on vacation. Save your favorite providers and get real-time notifications of Part-Time, Full-Time or Drop-In openings.\n\nSearch a list of Camps and Programs in your area or check out Upcoming Events!' },
    { icon: Calendar, title: lang === 'fr' ? 'Disponibilité et événements' : 'Live Availability and Events', desc: lang === 'fr' ? 'Soyez notifié immédiatement lorsque vos fournisseurs favoris publient une disponibilité. Réservez une place sans rendez-vous si vous avez besoin de garde! Partagez des calendriers pour que chacun soit informé.' : 'Get notified immediately when your favorite providers post an available opening and be able to reserve a Drop-In spot immediately if you are stuck for care! View local events or Create your own and share or invite others to join you! Shared calendars back and forth so when you book a family vacation, your provider can see you will be gone, or Providers can put Vacations or Sick days in their own calendars and notify the entire group immediately.' },
    { icon: MapPin, title: lang === 'fr' ? 'Vue carte' : 'Map View', desc: lang === 'fr' ? 'Voyez tous les fournisseurs sur une carte interactive dans votre région. Trouvez une gardienne près de chez vous, même en vacances!' : 'See all providers on an interactive map in your area. Want to find a babysitter that\'s close by while you\'re on vacation or out of town? You can do that here!' },
    { icon: Users, title: lang === 'fr' ? 'Profils de travailleurs' : 'Worker Profiles', desc: lang === 'fr' ? 'Parcourez les garderies, centres, pré-écoles, nounous, tuteurs, gardiennes, travailleurs de garderie, aidants, maisons de soins et infirmières... même les nettoyeurs... avec qualifications affichées.' : 'Browse Daycares, Centers, Pre-Schools, Nannies, Tutors, Babysitters, Daycare Workers, Caregivers, Care Homes and Nurses...even Cleaners... with posted qualifications.' },
    { icon: MessageCircle, title: lang === 'fr' ? 'Messagerie directe' : 'Direct Messaging', desc: lang === 'fr' ? 'Communiquez directement avec les fournisseurs, parents, membres de la famille et travailleurs en toute sécurité. Les fournisseurs peuvent créer des discussions de groupe pour informer tous leurs parents immédiatement de tout changement ou événement à venir. Ils peuvent même partager des photos directement avec le groupe des activités quotidiennes.' : 'Communicate directly with providers, parents, family members and workers securely. Providers can create group chats to inform all their parents immediately of any changes or upcoming events. Providers can even share pictures directly to the group of the daily activities.' },
    { icon: BookOpen, title: lang === 'fr' ? 'Centre de ressources' : 'Resource Hub', desc: lang === 'fr' ? 'Accédez aux plans de repas recommandés, livres, jouets, pages à colorier, activités adaptées à l\'âge, suggestions et liens tels que les financements gouvernementaux, l\'aide médicale et plus encore.' : 'Access to recommended meal plans, books, toys, coloring pages, age appropriate activities, upload suggestions and links such as gov\'t fundings, medical assistance and more.' },
    { icon: FileSignature, title: lang === 'fr' ? 'Rapports, facturation et signatures' : 'Reports, Invoicing & E-Signatures', desc: lang === 'fr' ? 'Les fournisseurs peuvent envoyer aux parents des rapports quotidiens, des formulaires de présence mensuels préremplis, des factures et des relevés fiscaux de fin d\'année. Les décharges, formulaires de consentement et contrats peuvent être signés directement dans l\'application - sans impression, numérisation ni paperasse.' : 'Providers can send parents daily reports, auto-filled monthly attendance forms, invoices and year-end tax statements. Waivers, consent forms and contracts can be reviewed and signed right in the app - no printing, scanning or paperwork.' },
  ];

  return (
    <div className="relative min-h-screen overflow-x-hidden">
      <div className="blob blob-1" />
      <div className="blob blob-2" />
      <div className="blob blob-3" />

      {/* Nav */}
      <nav className="relative z-50 px-4 sm:px-6 py-4 sm:py-5">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <BrandLogo size={40} className="rounded-xl sm:rounded-2xl" />
            <span className="text-lg sm:text-2xl font-semibold" style={{ fontFamily: 'Fredoka' }} data-testid="brand-logo">
              <span className="hidden sm:inline">ChildCareConnections</span>
              <span className="sm:hidden">CCC</span>
            </span>
            <span className="flex items-center gap-0.5">
            <a
              href="https://www.facebook.com/profile.php?id=61575771127335"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="facebook-link"
              aria-label="Visit ChildCareConnections on Facebook"
              title="Follow us on Facebook"
              className="flex items-center justify-center w-8 h-8 rounded-full text-[#1877F2] hover:bg-[#1877F2]/10 transition-colors shrink-0"
            >
              <FacebookIcon className="w-5 h-5" />
            </a>
            <a
              href="https://www.instagram.com/childcareconnections.ca"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="instagram-link"
              aria-label="Visit ChildCareConnections on Instagram"
              title="Follow us on Instagram"
              className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-[#d6249f]/10 transition-colors shrink-0"
            >
              <InstagramIcon className="w-5 h-5" />
            </a>
            </span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-3">
            {/* Language Selector */}
            <div className="relative" ref={langRef}>
              <button onClick={() => setShowLangMenu(!showLangMenu)} className="flex items-center gap-1 px-2 py-1.5 rounded-xl hover:bg-white/30 transition-colors text-slate-500">
                <Globe className="w-4 h-4" />
                <span className="text-xs font-medium hidden sm:inline">{languages.find(l => l.code === lang)?.label?.split(' ')[0] || 'EN'}</span>
              </button>
              {showLangMenu && (
                <div className="fixed sm:absolute left-4 right-4 sm:left-auto sm:right-0 top-14 sm:top-full sm:mt-1 sm:w-48 bg-white/95 backdrop-blur-xl border border-white/50 rounded-2xl shadow-2xl py-1 overflow-hidden" style={{ zIndex: 9999 }}>
                  {languages.map(l => (
                    <button key={l.code} onClick={(e) => { e.stopPropagation(); localStorage.setItem('lang', l.code); localStorage.setItem('lang_manual', 'true'); setShowLangMenu(false); setTimeout(() => window.location.reload(), 50); }}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${lang === l.code ? 'bg-violet-50 text-violet-700 font-medium' : 'text-slate-600 hover:bg-slate-50'}`}>
                      <span className="text-base">{l.flag}</span>{l.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {user ? (
              <Button data-testid="nav-dashboard-btn" onClick={() => navigate('/dashboard')}
                className="rounded-full text-white shadow-lg shadow-violet-500/30 px-4 sm:px-6 text-xs sm:text-sm"
                style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 40%, #14b8a6 60%, #0d9488 100%)' }}>
                {t('dashboard')}
              </Button>
            ) : (
              <>
                <Button data-testid="nav-login-btn" variant="ghost" onClick={() => navigate('/auth')}
                  className="rounded-full hover:bg-white/30 text-slate-700 text-xs sm:text-sm px-3 sm:px-4">
                  {t('signIn')}
                </Button>
                <Button data-testid="nav-register-btn" onClick={() => navigate('/auth?mode=register')}
                  className="rounded-full text-white shadow-lg shadow-violet-500/30 px-4 sm:px-6 text-xs sm:text-sm"
                  style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 40%, #14b8a6 60%, #0d9488 100%)' }}>
                  {t('getStarted')}
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* SVG gradient for feature icons */}
      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <defs>
          <linearGradient id="icon-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#8b5cf6" />
            <stop offset="100%" stopColor="#14b8a6" />
          </linearGradient>
        </defs>
      </svg>

      {/* Account Deleted Confirmation */}
      {showDeleted && (
        <div className="relative z-20 mx-auto max-w-lg mt-4 px-4">
          <div className="glass-panel rounded-2xl p-6 text-center border-l-4 border-emerald-400">
            <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Fredoka' }}>Account Deleted</h3>
            <p className="text-sm text-slate-500 mb-3">Your account has been permanently deleted. We&apos;re sorry to see you go.</p>
            <button onClick={() => setShowDeleted(false)} className="text-xs text-violet-500 hover:underline">Dismiss</button>
          </div>
        </div>
      )}

      {/* Hero */}
      <section className="relative z-10 px-6 pt-6 pb-20">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 space-y-6 animate-fade-in">
            {/* Mobile only: contest banner + clickable rating summary at the very top */}
            <div className="lg:hidden space-y-3" data-testid="mobile-hero-reviews">
              <ReviewPromoBox variant="hero" contestRules />
              <ReviewCarousel variant="rating-only" />
            </div>
            <h1 className="text-5xl md:text-7xl font-semibold tracking-tight text-slate-900 leading-tight" style={{ fontFamily: 'Fredoka' }}>
              {lang === 'fr' ? 'Trouvez la garde' : 'Find the perfect'}
              <span className="text-violet-500 block">{lang === 'fr' ? "d'enfants parfaite" : 'childcare'}</span>
              {lang === 'fr' ? 'pour votre famille' : 'for your little one'}
            </h1>
            <p className="text-lg md:text-xl text-slate-600 max-w-xl leading-relaxed">
              {lang === 'fr' ? "Une application centralisée conçue pour les fournisseurs de garde, les parents, les travailleurs, les camps et programmes — réunissant chaque aspect de la garde d'enfants en un seul endroit. La recherche de garde devient simple, rapide et sans stress. Désormais avec des rapports quotidiens et mensuels, la facturation des parents, des relevés fiscaux de fin d'année et des signatures électroniques dans l'application — de la recherche de garde à la paperasse de tous les jours, tout est au même endroit." : "A centralized app built for childcare providers, parents, workers, camps & programs — bringing every part of the childcare journey into one place. Searching for childcare becomes simple, fast, and stress-free. Now with daily & monthly reports, parent invoicing, year-end tax statements and in-app e-signatures — from finding care to the everyday paperwork, it all lives in one place."}
            </p>
            <div className="flex flex-wrap gap-4">
              <Button
                data-testid="hero-get-started-btn"
                onClick={() => navigate(user ? '/dashboard' : '/auth?mode=register')}
                className="rounded-full bg-violet-500 text-white hover:bg-violet-600 shadow-[0_4px_14px_0_rgba(139,92,246,0.39)] hover:shadow-[0_6px_20px_rgba(139,92,246,0.23)] hover:-translate-y-0.5 transition-all duration-200 px-8 py-6 text-lg"
              >
                Get Started Free <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                data-testid="hero-browse-btn"
                variant="ghost"
                onClick={() => navigate(user ? '/search' : '/browse')}
                className="rounded-full bg-white/50 text-slate-800 border border-white/50 backdrop-blur-sm hover:bg-white/80 px-8 py-6 text-lg"
              >
                {t('browseProviders')}
              </Button>
            </div>
            {/* Trusted badge — below the Get Started buttons */}
            <div className="pt-1">
              <div className="inline-flex items-center gap-2 glass-panel rounded-full px-4 py-2 text-sm text-slate-600" data-testid="trusted-badge">
                <Star className="w-4 h-4 text-amber-400" fill="#fbbf24" />
                Trusted by 2,000+ families
              </div>
            </div>
          </div>
          <div className="lg:col-span-5 space-y-6 animate-fade-in animate-fade-in-delay-2">
            {/* Reviews — desktop/tablet only; on mobile the banner + rating live at the top of the hero */}
            <div className="hidden lg:block space-y-6">
              <ReviewPromoBox variant="hero" contestRules />
              <ReviewCarousel variant="hero-rail" />
            </div>
            <div className="relative">
              <div className="glass-panel rounded-3xl overflow-hidden p-3">
                <img
                  src="https://customer-assets.emergentagent.com/job_nurture-place/artifacts/mdn77swf_Landing%20Page%20picture.PNG"
                  alt="Child holding parent's hand walking to daycare"
                  className="rounded-2xl w-full h-80 object-cover"
                />
              </div>
              {/* Floating card */}
              <div className="absolute -bottom-6 -left-6 glass-panel rounded-2xl p-4 flex items-center gap-3 animate-fade-in animate-fade-in-delay-3">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-700">Active Providers</p>
                  <p className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Fredoka' }}>500+</p>
                </div>
              </div>
              <div className="absolute top-6 -right-4 glass-panel rounded-2xl p-3 flex items-center gap-2 animate-fade-in animate-fade-in-delay-4">
                <div className="flex -space-x-2">
                  {[1,2,3].map(i => (
                    <div key={i} className="w-8 h-8 rounded-full bg-violet-200 border-2 border-white flex items-center justify-center text-xs text-violet-600 font-medium">
                      {['S','M','E'][i-1]}
                    </div>
                  ))}
                </div>
                <span className="text-sm text-slate-600 font-medium">Join today</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Elder Care Hero */}
      <section className="relative z-10 px-6 pt-8 pb-20">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 lg:order-1 order-2 animate-fade-in animate-fade-in-delay-2">
            <div className="relative">
              <div className="glass-panel rounded-3xl overflow-hidden p-3" style={{ borderColor: 'rgba(13,148,136,0.15)' }}>
                <img
                  src="/ecc-hero.png"
                  alt="Caregiver holding elderly person's hand walking towards a care home"
                  className="rounded-2xl w-full h-80 object-cover"
                />
              </div>
              {/* Floating card - caregivers */}
              <div className="absolute -bottom-6 -right-6 glass-panel rounded-2xl p-4 flex items-center gap-3 animate-fade-in animate-fade-in-delay-3" style={{ borderColor: 'rgba(13,148,136,0.15)' }}>
                <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center">
                  <Heart className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-700">Trusted Caregivers</p>
                  <p className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Fredoka' }}>200+</p>
                </div>
              </div>
              <div className="absolute -top-4 -left-4 glass-panel rounded-2xl p-3 flex items-center gap-2 animate-fade-in animate-fade-in-delay-4" style={{ borderColor: 'rgba(13,148,136,0.15)' }}>
                <div className="flex -space-x-2">
                  {[1,2,3].map(i => (
                    <div key={i} className="w-8 h-8 rounded-full bg-teal-200 border-2 border-white flex items-center justify-center text-xs text-teal-600 font-medium">
                      {['R','M','J'][i-1]}
                    </div>
                  ))}
                </div>
                <span className="text-sm text-slate-600 font-medium">Families trust us</span>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 lg:order-2 order-1 space-y-8 animate-fade-in">
            <div className="inline-flex items-center gap-2 glass-panel rounded-full px-4 py-2 text-sm text-slate-600" style={{ borderColor: 'rgba(13,148,136,0.15)' }}>
              <Heart className="w-4 h-4 text-teal-500" />
              Compassionate elder care starts here
            </div>
            <h1 className="text-5xl md:text-7xl font-semibold tracking-tight text-slate-900 leading-tight" style={{ fontFamily: 'Fredoka' }}>
              {lang === 'fr' ? 'Trouvez les soins' : 'Find the right'}
              <span className="text-teal-500 block">{lang === 'fr' ? 'parfaits pour vos' : 'elder care'}</span>
              {lang === 'fr' ? 'proches' : 'for your loved ones'}
            </h1>
            <p className="text-lg md:text-xl text-slate-600 max-w-xl leading-relaxed">
              {lang === 'fr' ? "Connectez-vous avec des maisons de soins, des aides-soignants et des programmes pour personnes agees de confiance, le tout en un seul endroit. Trouvez la bonne solution pour votre famille en toute confiance." : "Connect with trusted care homes, assisted living facilities, personal support workers, and senior programs — all in one place. Find the right care solution for your family with confidence and peace of mind."}
            </p>
            <div className="flex flex-wrap gap-4">
              <Button
                data-testid="hero-ecc-get-started-btn"
                onClick={() => navigate(user ? '/dashboard' : '/auth?mode=register&segment=eldercare')}
                className="rounded-full bg-teal-500 text-white hover:bg-teal-600 shadow-[0_4px_14px_0_rgba(13,148,136,0.39)] hover:shadow-[0_6px_20px_rgba(13,148,136,0.23)] hover:-translate-y-0.5 transition-all duration-200 px-8 py-6 text-lg"
              >
                Get Started Free <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                data-testid="hero-ecc-browse-btn"
                variant="ghost"
                onClick={() => navigate(user ? '/search?segment=eldercare' : '/browse?segment=eldercare')}
                className="rounded-full bg-white/50 text-slate-800 border border-white/50 backdrop-blur-sm hover:bg-white/80 px-8 py-6 text-lg"
              >
                Browse Care Providers
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="relative z-10 px-6 py-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-medium uppercase tracking-widest mb-3" style={{ background: 'linear-gradient(135deg, #8b5cf6, #14b8a6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{t('whyUs')}</p>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Fredoka' }}>
              Everything you need
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <div
                key={f.title}
                className="glass-panel glass-panel-hover rounded-3xl p-8 group cursor-default animate-fade-in"
                style={{ animationDelay: `${i * 0.1}s`, opacity: 0 }}
                data-testid={`feature-card-${i}`}
              >
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300"
                  style={{ background: 'linear-gradient(135deg, rgba(139,92,246,0.12) 0%, rgba(20,184,166,0.12) 100%)' }}>
                  <f.icon className="w-7 h-7" style={{ stroke: 'url(#icon-gradient)' }} />
                </div>
                <h3 className="text-xl font-medium text-slate-900 mb-2" style={{ fontFamily: 'Fredoka' }}>{f.title}</h3>
                <p className="text-slate-600 leading-relaxed whitespace-pre-line">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 px-6 py-20">
        <div className="max-w-4xl mx-auto">
          <div className="glass-panel rounded-3xl p-12 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-violet-500/10 via-transparent to-rose-500/10" />
            <div className="relative z-10">
              <h2 className="text-3xl md:text-5xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Fredoka' }}>
                {lang === 'fr' ? 'Prêt à trouver les soins parfaits?' : 'Ready to find the perfect care for your loved ones?'}
              </h2>
              <p className="text-lg text-slate-600 mb-8 max-w-xl mx-auto">
                {t('joinThousands')}
              </p>
              <Button
                data-testid="cta-get-started-btn"
                onClick={() => navigate(user ? '/dashboard' : '/auth?mode=register')}
                className="rounded-full text-white shadow-[0_4px_14px_0_rgba(139,92,246,0.39)] px-8 py-6 text-lg"
                style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 40%, #14b8a6 60%, #0d9488 100%)' }}
              >
                Start for Free <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      {/* Footer links rendered globally by AppFooter */}
      <div className="relative z-10 px-6 py-4">
        <div className="flex items-center justify-center gap-2">
          <BrandLogo size={16} />
          <span className="text-sm text-slate-500">ChildCareConnections & ElderCareConnections 2026</span>
        </div>
      </div>
    </div>
  );
}
